import { Stat } from "views/applications-id-report/components/Stat";
import { has, toMoney } from "shared/utils";
import { Show } from "shared/components";
import { CombinedIncomeReport, TotalSummary } from "../types";
import { ReverifyRequest, VerificationStatus } from "shared/types";
import { SummaryUpdated } from "../components/SummaryUpdated";
import { CriticalDataMissingWarning } from "../components/CriticalDataMissingWarning";
import {
  getDebtGrossIncomeRatio,
  getNetMonthlyIncome,
  getSelfReportedGrossMonthly,
  getVerifiedIncomeSourcesCount,
  isIncomeVerificationSkipped,
  showValueOrPlaceholder
} from "../utils";
import { CombinedIncome } from "../types/income";
import { ProgressReportView } from "views/applications-id-report/components/ReportsFallback/ProgressReportView";
import { ReverifyGlobalWarning } from "./ReverifyGlobalWarning";

export default function IncomeSummary({
  incomes,
  pendingReverifyRequests,
  completedReverifyRequests,
  summary,
  combinedIncomeReport,
  hasCreditReport,
  verificationStatus = VerificationStatus.Pending,
  isSummaryPending
}: {
  incomes: CombinedIncome[];
  pendingReverifyRequests: ReverifyRequest[];
  completedReverifyRequests: ReverifyRequest[];
  summary: TotalSummary;
  combinedIncomeReport: CombinedIncomeReport;
  hasCreditReport: boolean;
  verificationStatus?: VerificationStatus;
  isSummaryPending: boolean;
}) {
  const isSkipped = incomes.every(isIncomeVerificationSkipped);
  const netMonthlyIncome = getNetMonthlyIncome(combinedIncomeReport, summary);
  const debtGrossIncomeRatio = getDebtGrossIncomeRatio(summary);

  if (isSummaryPending)
    return (
      <ProgressReportView className="max-w-xl text-center my-4">
        <p className="text-content-500 font-bold text-md mb-2">
          Income history report is in progress
        </p>
        <p className="text-content-300 font-medium text-sm">
          {incomes.length === 0
            ? "No income data yet — waiting for the applicant to begin this section"
            : "Partial data shown — waiting for the applicant to complete the application"}
        </p>
      </ProgressReportView>
    );

  return (
    <>
      <ReverifyGlobalWarning
        incomes={incomes}
        pendingReverifyRequests={pendingReverifyRequests}
        completedReverifyRequests={completedReverifyRequests}
        verificationStatus={verificationStatus}
      />
      <div className="md:p-6 pt-3 pl-4 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Stat
            title="Verified gross monthly"
            value={showValueOrPlaceholder(
              toMoney(summary.verified_gross_monthly_total),
              !isSkipped
            )}
          />
          <Stat
            title="Verified net monthly"
            value={showValueOrPlaceholder(netMonthlyIncome, !isSkipped)}
          />
          <Show if={hasCreditReport}>
            <Stat
              title="Debt to verified gross income ratio"
              value={showValueOrPlaceholder(debtGrossIncomeRatio, !isSkipped)}
            />
          </Show>

          <Stat
            title="Self-reported gross monthly"
            value={getSelfReportedGrossMonthly(combinedIncomeReport, summary)}
          />
          <Stat
            title="Monthly assistance amount"
            value={showValueOrPlaceholder(
              toMoney(summary.monthly_housing_assistance),
              Boolean(summary.monthly_housing_assistance)
            )}
          />
          <Stat
            title="Verified income sources"
            value={getVerifiedIncomeSourcesCount(incomes, summary)}
          />
        </div>
        <Show
          if={
            has(combinedIncomeReport.total_overrides?.gross_monthly_income) ||
            has(combinedIncomeReport.total_overrides?.net_monthly_income)
          }
        >
          <SummaryUpdated
            changes={combinedIncomeReport.total_overrides}
            original={{
              gross: summary.self_reported_gross_monthly_total,
              net: summary.verified_net_monthly_total
            }}
          />
        </Show>
        <CriticalDataMissingWarning incomes={incomes} />
      </div>
    </>
  );
}
